import { Pressable, View } from "react-native"
import { Link } from "expo-router"
import * as Burnt from "burnt"

import { MCIcon, Text } from "@src/components/core"
import { Alert } from "@src/components/ui"
import { ar } from "@src/i18n/ar"
import { formatPrice } from "@src/utils"
import { trpc } from "utils/trpc"

type DerivedProductCardProps = {
  dp: {
    id: number
    name: string
    description?: string | null
    upc: string
    price?: number | null
    type: string
    status: "active" | "inactive"
    value?: number | null
  }
  baseId: number
  baseStatus?: "active" | "inactive"
}

export function DerivedProductCard({ dp, baseId, baseStatus }: DerivedProductCardProps) {
  const utils = trpc.useUtils()
  const setStatus = trpc.products.setStatus.useMutation()

  const isInactive = dp.status === "inactive"
  const restoreDisabled = baseStatus !== "active"

  const handleDelete = () => {
    Alert.alert(ar.confirmDelete, ar.areYouSure, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.delete,
        style: "destructive",
        onPress: async () => {
          try {
            await setStatus.mutateAsync({ id: dp.id, status: "inactive" })
            utils.products.get.invalidate({ id: baseId })
            utils.products.list.invalidate()
            Burnt.toast({ title: ar.productDeleted })
          } catch (error) {
            Alert.alert(ar.error, error instanceof Error ? error.message : ar.somethingWentWrong)
          }
        },
      },
    ])
  }

  const handleRestore = () => {
    Alert.alert(ar.restoreProduct, ar.restoreProductConfirmation, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.restoreProduct,
        style: "default",
        onPress: async () => {
          try {
            await setStatus.mutateAsync({ id: dp.id, status: "active" })
            utils.products.get.invalidate({ id: baseId })
            utils.products.list.invalidate()
            Burnt.toast({ title: ar.productRestored })
          } catch (error) {
            Alert.alert(ar.error, error instanceof Error ? error.message : ar.somethingWentWrong)
          }
        },
      },
    ])
  }

  return (
    <View className={`py-3 border-b border-gray-100 md:border md:rounded md:p-4 ${isInactive ? "opacity-60" : ""}`}>
      <Link href={`/dashboard/products/${dp.id}`} asChild>
        <Pressable className={`active:opacity-70 ${dp.type === "WEIGHT_DEFINED_PRODUCT" ? "pointer-events-none" : ""}`}>
          <Text className="font-medium text-gray-900 ">{dp.name}</Text>
          {dp.description ? <Text className="text-sm text-gray-600 mt-1 ">{dp.description}</Text> : null}
        </Pressable>
      </Link>

      <View className="flex-row justify-between items-center mt-2">
        <Text className="text-sm text-gray-500 ">
          {ar.sku}: {dp.upc}
        </Text>
        {dp.price != null && (
          <Text className="text-sm font-semibold text-green-600">
            {formatPrice(dp.price)} {ar.egp}
          </Text>
        )}
      </View>

      <View className="flex-row justify-between items-center mt-2">
        {isInactive ? (
          <View className="flex-row items-center">
            <MCIcon name="delete" size={16} color="#9CA3AF" />
            <Text className="text-sm text-gray-500 font-medium ms-1">{ar.deleted}</Text>
          </View>
        ) : (
          <View />
        )}

        {isInactive ? (
          <Pressable onPress={handleRestore} disabled={restoreDisabled || setStatus.isPending} className={`px-2 py-1 active:opacity-70 ${restoreDisabled ? "opacity-40" : ""}`}>
            <Text className="text-sm text-green-600 font-bold">{ar.restoreProduct}</Text>
          </Pressable>
        ) : (
          <Pressable onPress={handleDelete} disabled={setStatus.isPending} className="px-2 py-1 active:opacity-70">
            <Text className="text-sm text-red-600 font-bold">{ar.delete}</Text>
          </Pressable>
        )}
      </View>
    </View>
  )
}
